import { type FunctionComponent, useRef, useContext, useState, useEffect, startTransition } from 'react';
import { MediaGalleryContext } from '../../../contexts/pdp/media-gallery.context';
import { isThreeSixtyImage, isVideo, type Media } from '../../../helpers/images/images.helper';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { DEFAULT_CARD_WIDTH, useUpdateMediaProduct } from '../../../hooks/pdp/media-gallery/media-gallery.hooks';
import { CarouselButton } from '../../carousel/carousel.component';
import { EmbeddedVideo } from '../../common-components/embedded-video/embedded-video.component';
import { ThreeSixtyImage } from '../../common-components/threesixty-image/threesixty-image.component';
import { VideoCard } from '../../common-components/video-card/video-card.component';
import { ZoomableImage } from '../../common-components/zoomable-image/zoomable-image.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import ZoomInIcon from '../../svgs/untitled-ui/zoom-in.svg';
import { ThumbnailCarousel } from '../thumbnail-carousel/thumbnail-carousel.component';
import { type CloudinaryOpts } from '../thumbnail-carousel/thumbnail-components/thumbnail-components.component';
import { cardSlider, cardSliderContent } from './image-gallery-carousel.css';

type GalleryCardProps = {
	item: Media;
	showZoomIcon: boolean;
	imageOptions: CloudinaryOpts;
	className?: string;
	videoStyling?: string;
	isSelected: boolean;
	skipLazyVideo?: boolean;
	shouldOpenVideoInModal: boolean;
	isFetchPriority?: boolean;
	onClick?: () => void;
	onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
	testId?: string;
	cardHeight?: string;
};

const GalleryCard: FunctionComponent<GalleryCardProps> = ({
	item,
	showZoomIcon,
	imageOptions,
	className,
	isSelected = false,
	skipLazyVideo = false,
	videoStyling = '',
	shouldOpenVideoInModal,
	isFetchPriority = false,
	onClick,
	onLoad,
	testId,
	cardHeight
}) => {
	return (
		<div
			className={`w-100 flex items-center justify-center ${cardSliderContent} ${cardHeight}`}
			onClick={onClick}
			onKeyPress={handleKeys(['Enter', ' '], () => onClick?.())}
			tabIndex={0}
			role="button"
			data-testid={testId}>
			{isVideo(item) ? (
				shouldOpenVideoInModal ? (
					<div className={videoStyling}>
						<VideoCard
							video={item}
							shouldLoadEagerly={skipLazyVideo}
							shouldPause={!isSelected}
							previewWidthPx={imageOptions.options.width}
							previewHeightPx={imageOptions.options.height}
							isSimpleVideoModal={true}
							autoPlay={true}
						/>
					</div>
				) : (
					<div className={`center w-100 ${videoStyling}`}>
						<EmbeddedVideo video={item} shouldPause={!isSelected} shouldLoadEagerly={skipLazyVideo} />
					</div>
				)
			) : isThreeSixtyImage(item) ? (
				<ThreeSixtyImage className={className} options={imageOptions.options} id={item.id} image={item.thumbnail} />
			) : (
				<>
					<ZoomableImage
						className={className}
						publicID={item.id}
						description={item.description}
						limitHeight={true}
						options={imageOptions?.options}
						bypassLazyLoad={true}
						variations={imageOptions?.variations}
						actualImageProps={{ fetchpriority: isFetchPriority ? 'high' : 'auto', onLoad, 'aria-label': item.description }}
					/>
					{showZoomIcon && (
						<ZoomInIcon className={'theme-grey bg-theme-white-o80 br2 absolute z-5 f2 right-0 bottom-0 mv3 mh1 dn-ns dn-p'} />
					)}
				</>
			)}
		</div>
	);
};

export type ImageGalleryCarouselProps = {
	items: Media[];
	selectedItemId?: string;
	thumbnailMode: 'hide' | 'show' | 'desktop' | 'mobile';
	onSelectedImageChange?: (item: Media) => void;
	onClick?: () => void;
	galleryImageOptions: CloudinaryOpts;
	videoStyling?: string;
	isInModal?: boolean;
	shouldOpenVideoInModal: boolean;
	cardWrapper?: FunctionComponent<{ index: number }>;
	overrideHero?: Media | null;
	cardHeight?: string;
};

export const THUMBNAIL_MODE_MAP = {
	hide: 'dn',
	show: 'db',
	desktop: 'dn db-l',
	mobile: 'db dn-ns'
};

export const ImageGalleryCarousel: FunctionComponent<ImageGalleryCarouselProps> = ({
	items,
	selectedItemId,
	thumbnailMode = 'desktop',
	onSelectedImageChange,
	onClick,
	galleryImageOptions,
	videoStyling,
	isInModal = false,
	shouldOpenVideoInModal,
	cardWrapper: CardWrapper,
	overrideHero = null,
	cardHeight
}) => {
	const { activeIndex, translateGalleryPercentage, thumbnailCarouselWidth, slidesPerView, dispatch } = useContext(MediaGalleryContext);
	const [heroLoaded, setHeroLoaded] = useState(false);
	const { updateCarouselSlides } = useUpdateMediaProduct(items);

	const handleLoad = async (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		const img = e.currentTarget;

		if (img) {
			await img.decode().finally(() => {
				startTransition(() => setHeroLoaded(true));
			});
		}
	};

	const handleNavigateForward = () => {
		updateCarouselSlides(activeIndex + 1, DEFAULT_CARD_WIDTH, slidesPerView);
		onSelectedImageChange?.(items[activeIndex + 1]);
	};

	const handleNavigateBack = () => {
		updateCarouselSlides(activeIndex - 1, DEFAULT_CARD_WIDTH, slidesPerView);
		onSelectedImageChange?.(items[activeIndex - 1]);
	};

	// Mobile touch event refs
	const touchStartXRef = useRef<number>(0);
	const touchMoveXRef = useRef<number>(0);
	const carouselContainerRef = useRef<HTMLDivElement>(null);
	const [prevTouchMoveValue, setPrevTouchMoveValue] = useState(0);

	const handleGallerySwipe = () => {
		// Keeping track of prev touch move value, so carousel doesn't slide on click
		setPrevTouchMoveValue(touchMoveXRef.current);

		const touchMoveAmount = touchStartXRef.current - touchMoveXRef.current;
		if (Math.abs(touchMoveAmount) > 25 && touchMoveXRef.current !== prevTouchMoveValue) {
			// Direction of the swipe determines next or previous item
			const photoIndexChange = Math.sign(touchMoveAmount);
			// Clamp new index between 0 and last item index
			const newPhotoIndex = Math.max(0, Math.min(activeIndex + photoIndexChange, items.length - 1));
			updateCarouselSlides(newPhotoIndex, DEFAULT_CARD_WIDTH, slidesPerView);
			onSelectedImageChange?.(items[newPhotoIndex]);
		}
	};

	// needs to update slidesPerView only when thumbnailCarousel is visible
	useEffect(() => {
		if (thumbnailCarouselWidth > 0) {
			dispatch({ type: 'SET_SLIDES_PER_VIEW', payload: Math.round(thumbnailCarouselWidth / DEFAULT_CARD_WIDTH) });
		}
	}, [thumbnailCarouselWidth, dispatch]);

	return (
		items.length > 0 && (
			<>
				<div className="chromatic-ignore mb5 flex justify-center items-center" data-testid="image-gallery-carousel">
					{isInModal && (
						<div className="pr2 dn db-ns">
							<CarouselButton
								arrowDirection="left"
								onClick={handleNavigateBack}
								shouldLock={activeIndex === 0}
								isMediaGallery
							/>
						</div>
					)}
					<div className={`relative overflow-hidden w-100 ${cardHeight}`}>
						<div
							className={`flex h-100 ${cardSlider}`}
							ref={carouselContainerRef}
							onTouchStart={(e) => (touchStartXRef.current = e.targetTouches[0].clientX)}
							onTouchMove={(e) => (touchMoveXRef.current = e.targetTouches[0].clientX)}
							onTouchEnd={handleGallerySwipe}
							style={{
								transform: `translate(${translateGalleryPercentage}%)`,
								scrollBehavior: 'smooth',
								touchAction: 'pan-y'
							}}
							data-testid="image-gallery-carousel-gallery-card-slider">
							{/* HERO IMAGE */}
							{overrideHero ? (
								<div
									className={`w-100 flex items-center justify-center ${cardSliderContent} ${cardHeight}`}
									data-testid="current-override-image">
									<CloudinaryImage
										publicID={overrideHero.id}
										description={overrideHero.description}
										options={galleryImageOptions.options}
									/>
								</div>
							) : (
								<GalleryCard
									item={items[0]}
									showZoomIcon={onClick !== undefined}
									imageOptions={galleryImageOptions}
									isSelected={selectedItemId === items[0].id}
									skipLazyVideo={isInModal}
									shouldOpenVideoInModal={shouldOpenVideoInModal}
									videoStyling={videoStyling}
									isFetchPriority={!isInModal}
									onLoad={handleLoad}
									onClick={onClick}
									testId={`image-gallery-carousel-item-0`}
								/>
							)}
							{/* SECONDARY IMAGES */}
							{heroLoaded &&
								items
									.slice(1)
									.map((item, index) => (
										<GalleryCard
											item={item}
											showZoomIcon={onClick !== undefined}
											imageOptions={galleryImageOptions}
											isSelected={selectedItemId === item.id}
											skipLazyVideo={isInModal}
											shouldOpenVideoInModal={shouldOpenVideoInModal}
											videoStyling={videoStyling}
											onClick={onClick}
											testId={`image-gallery-carousel-item-${index + 1}`}
											key={`${item.id}-${index + 1}`}
										/>
									))}
						</div>
					</div>
					{isInModal && (
						<div className="pl2 dn db-ns">
							<CarouselButton
								arrowDirection="right"
								onClick={handleNavigateForward}
								shouldLock={activeIndex === (items.length && items.length - 1)}
								isMediaGallery
							/>
						</div>
					)}
				</div>

				<div className={`${THUMBNAIL_MODE_MAP[thumbnailMode]} dn-p`}>
					{items.length > 1 && thumbnailMode !== 'hide' && (
						<ThumbnailCarousel
							items={items}
							cardWrapper={CardWrapper}
							onSelectedImageChange={onSelectedImageChange}
							slidesPerView={slidesPerView}
						/>
					)}
				</div>
			</>
		)
	);
};
